import { api } from './client';

export type TravelMode = 'WALKING' | 'DRIVING' | 'BICYCLING' | 'TRANSIT';

export interface LegRequest {
  originLat?:     number;
  originLng?:     number;
  originPlaceId?: string;
  originAddress?: string;
  destLat?:       number;
  destLng?:       number;
  destPlaceId?:   string;
  destAddress?:   string;
  mode:           TravelMode;
}

export interface RouteLeg {
  durationSec: number;
  distanceM:   number;
  polyline:    string;   // Google encoded polyline
  mode:        TravelMode;
}

/** ADR-0031: how a leg between two destinations is crossed when it isn't routed. */
export type TransferKind = 'FLIGHT' | 'TRANSFER';

/** `FERRY` is never requested — it's what a crossing turns out to be. */
export type SegmentMode = TravelMode | 'FERRY' | TransferKind;

/**
 * Legs are drawn segment by segment. Usually there's one; a leg that crosses
 * water comes back as three — drive to the port, sail, carry on.
 */
export interface ItinerarySegment {
  mode:          SegmentMode;
  durationSec:   number;
  distanceM:     number;
  polyline:      string;
  /** Google's wording for the crossing, e.g. "Take the ferry to Rottnest Island". */
  instructions?: string | null;
}

/** A leg of the itinerary route as the server computed it. */
export interface ItineraryLeg {
  fromActivityId: string;
  toActivityId:   string;
  /** What the server asked Google for — differs from `mode` when it had no route. */
  requestedMode:  TravelMode;
  mode:           TravelMode;
  durationSec:    number;
  distanceM:      number;
  polyline:       string;
  segments:       ItinerarySegment[];
}

/** ADR-0031: a flight or transfer leg — a straight line, no travel time. */
export interface ItineraryTransfer {
  legIdx: number;
  kind:   TransferKind;
  label:  string | null;
  leg:    ItineraryLeg;
}

export interface ItineraryRouteDay {
  dayId:            string;
  dayNumber:        number;
  /** Ordered stops the legs connect — leg i goes from stop i to stop i+1. */
  activityIds:      string[];
  legs:             (ItineraryLeg | null)[];
  transfers?:       ItineraryTransfer[];
  totalDurationSec: number;
  totalDistanceM:   number;
}

export interface ItineraryRouteRequest {
  /** Omit for every day of the trip that has at least two stops. */
  dayId?:       string;
  /** Stop order not yet persisted (a drag in progress). Only with `dayId`. */
  activityIds?: string[];
  /** Per-leg mode overrides; `null` entries let the server decide. */
  modes?:       (TravelMode | null)[];
  /**
   * Read the cache and nothing else: uncached legs come back null and the
   * request cannot cost anything. ADR-0005 — a view whose legs multiply with
   * the days it shows paints what is already paid for, and asks before more.
   */
  cachedOnly?:  boolean;
}

export const directionsApi = {
  fetchLegs: (tripId: string, legs: LegRequest[]) =>
    api.post<{ legs: (RouteLeg | null)[] }>(
      `/trips/${tripId}/directions/legs`,
      { legs },
    ).then(r => r.data.legs),

  // The server owns stop order, waypoint resolution and travel modes — web and
  // mobile just render what comes back. See server/src/services/route.service.ts.
  route: (tripId: string, body: ItineraryRouteRequest = {}) =>
    api.post<{ days: ItineraryRouteDay[] }>(
      `/trips/${tripId}/directions/route`,
      body,
    ).then(r => r.data.days.map(withTransfers)),
};

/**
 * ADR-0031: the server leaves a transfer's slot in `legs` null, for installed
 * apps that predate it, and sends the leg in `transfers`. Put it back here, once,
 * so everything downstream sees an answered leg and not one waiting to be bought.
 */
export function withTransfers(day: ItineraryRouteDay): ItineraryRouteDay {
  if (!day.transfers?.length) return day;
  const legs = [...day.legs];
  for (const tr of day.transfers) legs[tr.legIdx] = tr.leg;
  return { ...day, legs };
}
